/**
 * CustomFontFamilyUI Plugin for CKEditor
 *
 * This plugin provides the user interface for selecting and applying custom font families to text in the CKEditor.
 * It integrates with the editor's toolbar, dropdowns, and menu system to allow users to easily change the font family 
 * of the selected text.
 *
 * ## Features:
 * - **Font Family Dropdown**: 
 *   - Displays a dropdown of available font families.
 *   - Allows users to select and apply a font family to the selected text.
 * - **Menu Bar Integration**:
 *   - Adds a button to the editor's menu bar that opens the font family dropdown.
 * - **Localized Font Family Options**:
 *   - Supports localization of the font family options, ensuring that they display in the user's preferred language.
 * - **UI Commands**:
 *   - Uses commands to apply the selected font family when a user selects an option from the dropdown or menu.
 *
 * ## Usage:
 * The plugin works by providing a dropdown button in the toolbar and a menu bar integration for selecting font families. 
 * The user can select a font family, and the editor will apply it to the selected text. It uses the `CustomFontFamily` 
 * command to apply changes.
 * 
 * Example UI integration:
 * ```javascript
 * editor.ui.componentFactory.add(CKPlugins.CustomFontFamily, locale => {
 *     // Create dropdown UI for font family selection
 * });
 * ```
 *
 * ## Configuration:
 * The available font families can be configured by setting the `options` property within the `CKPlugins.CustomFontFamily` configuration.
 * Example:
 * ```javascript
 * editor.config.set(CKPlugins.CustomFontFamily, {
 *     options: CUSTOM_FONT_FAMILY // List of available font families
 * });
 * ```
 *
 * ## Implementation Details:
 * - **Dropdown UI**: Uses `createDropdown`, `addListToDropdown`, and other utilities to construct a dropdown list of font family options.
 * - **Menu Bar UI**: Creates a menu bar entry that also allows users to select and apply a font family.
 * - **Localized Options**: The options are localized for the editor's language by using the `t` method for translation.
 * - **Font Family Command**: Executes the `CustomFontFamily` command to apply the selected font family to the text.

 * ## References:
 * - CKEditor Plugin API: https://ckeditor.com/docs/ckeditor5/latest/api/module_core_plugin-Plugin.html
 * - CKEditor UI Components: https://ckeditor.com/docs/ckeditor5/latest/api/module_ui_componentfactory-ComponentFactory.html
 * 
 * @module CustomFontFamilyUI
 * @exports CustomFontFamilyUI
 */

import { CKPlugins } from '../../constants/constants';
import { normalizeOptions } from './fontFamilyEditing';

const {
	ViewModel,
	createDropdown,
	addListToDropdown,
	MenuBarMenuView,
	MenuBarMenuListView,
	MenuBarMenuListItemView,
	MenuBarMenuListItemButtonView,
    Plugin,
    Collection
} = window.ClassicEditor;


const fontFamilyIcon = '<svg class="ck ck-icon ck-reset_all-excluded ck-icon_inherit-color ck-button__icon" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M11.03 3h6.149a.75.75 0 1 1 0 1.5h-5.514L11.03 3zm1.27 3h4.879a.75.75 0 1 1 0 1.5h-4.244L12.3 6zm1.27 3h3.609a.75.75 0 1 1 0 1.5h-2.973L13.57 9zm-2.754 2.5L8.038 4.785 5.261 11.5h5.555zm.62 1.5H4.641l-1.666 4.028H1.312l5.789-14h1.875l5.789 14h-1.663L11.436 13z"></path></svg>';

export default class CustomFontFamilyUI extends Plugin {

	static get pluginName() {
		return 'CustomFontFamilyUI';
	}

	static get isOfficialPlugin() {
		return true;
	}

	init() {
		const editor = this.editor;
		const t = editor.t;

		const options = this._getLocalizedOptions();

		const command = editor.commands.get(CKPlugins.CustomFontFamily);
		const accessibleLabel = t('Font Family');
		const listOptions = _prepareListOptions(options, command);

		// Register UI component.
		editor.ui.componentFactory.add(CKPlugins.CustomFontFamily, locale => {
			const dropdownView = createDropdown(locale);

			addListToDropdown(dropdownView, listOptions as any, {
				role: 'menu',
				ariaLabel: accessibleLabel
			});

			dropdownView.buttonView.set({
				label: accessibleLabel,
				icon: fontFamilyIcon,
				tooltip: true
			});

			dropdownView.extendTemplate({
				attributes: {
					class: 'ck-font-family-dropdown'
				}
			});

			dropdownView.bind('isEnabled').to(command);

			// Execute command when an item from the dropdown is selected.
			this.listenTo(dropdownView, 'execute', evt => {
				editor.execute( (( evt.source ) as any).commandName, { value: (( evt.source) as any).commandParam } );
				editor.editing.view.focus();
			});

			return dropdownView;
		} );



		editor.ui.componentFactory.add(`menuBar:${CKPlugins.CustomFontFamily}`, locale => {
			const menuView = new MenuBarMenuView( locale );

			menuView.buttonView.set({
				label: accessibleLabel,
				icon: fontFamilyIcon
			});

			menuView.bind('isEnabled').to(command);

			const listView = new MenuBarMenuListView(locale);

			for ( const definition of listOptions ) {
				const listItemView = new MenuBarMenuListItemView(locale, menuView);
				const buttonView = new MenuBarMenuListItemButtonView(locale);

				buttonView.set({
					role: 'menuitemradio',
					isToggleable: true,
					class: 'Pepsi'
				});

				buttonView.bind(...Object.keys(definition.model) as any).to(definition.model);
				buttonView.delegate('execute').to(menuView);

				buttonView.on('execute', () => {
					editor.execute((definition.model).commandName, {
						value: (definition.model).commandParam
					});

					editor.editing.view.focus();
				} );

				listItemView.children.add(buttonView);
				listView.items.add(listItemView);
			}

			menuView.panelView.children.add(listView);

			return menuView;
		} );
	}

	_getLocalizedOptions() {
		const editor = this.editor;
		const t = editor.t;

		const options = normalizeOptions((editor.config.get(CKPlugins.CustomFontFamily) as any).options );

		return options.map(option => {
			if (option.title === 'Default') {
				option.title = t('Default');
			}

			return option;
		});
	}
}

/**
 * Prepares FontFamily dropdown items.
 */
function _prepareListOptions(options, command) {
	const itemDefinitions = new Collection();

	// Create dropdown items.
	for (const option of options) {
		const def = {
			type: 'button',
			model: new ViewModel({
				commandName: CKPlugins.CustomFontFamily,
				commandParam: option.model,
				label: option.title,
				role: 'menuitemradio',
				withText: true,
				class: option.class
			})
		};

		def.model.bind('isOn').to(command, 'value', value => {
			// "Default" or check in strict font-family converters mode.
			if (value === option.model) {
				return true;
			}

			if (!value || !option.model) {
				return false;
			}

			return value.split(',')[0].replace(/'/g, '').toLowerCase() === option.model.toLowerCase();
		} );


		itemDefinitions.add(def);
	}
	return itemDefinitions;
}
